import {Image, useProductOptions} from '@shopify/hydrogen';
import clsx from 'clsx';
import {ProductOptionsDropdown} from './ProductOptionsDropdown.client';
import {ProductOptionsSwatch} from './ProductOptionsSwatch.client';
import {DATA_TEST_IDS} from '~/lib/const';
import {useMedia} from 'react-use';

export function ProductOptions({
  values,
  availableValues,
  discountedValues,
  error,
  activeValue,
  ...props
}: {
  values: any[];
  availableValues: string[];
  discountedValues: string[];
  error?: boolean;
  activeValue: string | null;
  [key: string]: any;
} & React.ComponentProps<typeof OptionsGrid>) {
  if (/^(color|frame\/lens|frames|lens)$/i.test(props?.name?.toLowerCase())) {
    return (
      <OptionsColor
        values={values}
        activeValue={activeValue}
        error={error}
        discountedValues={discountedValues}
        {...props}
      />
    );
  }

  if (props.optionsType === DATA_TEST_IDS.PRODUCT_OPTIONS_DROPDOWN) {
    return (
      <ProductOptionsDropdown
        values={values}
        availableValues={availableValues}
        error={error}
        activeValue={activeValue}
        {...props}
      />
    );
  }

  if (props.optionsType === DATA_TEST_IDS.PRODUCT_OPTIONS_SWATCH) {
    return (
      <ProductOptionsSwatch
        values={values}
        availableValues={availableValues}
        error={error}
        activeValue={activeValue}
        {...props}
      />
    );
  }

  return <OptionsGrid values={values} {...props} />;
}

function OptionsColor({
  values,
  name,
  colorImages,
  activeValue,
  error,
  handleChange,
  discountedValues,
}: {
  values: string[];
  name: string;
  colorImages?: {[key: string]: any};
  activeValue: string | null;
  error?: boolean;
  handleChange: (name: string, value: string) => void;
  discountedValues: string[];
}) {
  const isWide = useMedia('(min-width: 768px)');
  return (
    <div data-testid={DATA_TEST_IDS.OPTIONS_COLOR} className="relative">
      <div className={'flex flex-wrap -ml-[9px] -mt-[9px]'}>
        {values.map((value) => {
          const formattedValue = decodeURIComponent(value).toLowerCase();
          const formattedActive = decodeURIComponent(
            activeValue!,
          ).toLowerCase();

          const checked = formattedValue === formattedActive;
          const id = `option-${name}-${value}`;
          const isDiscounted = discountedValues.includes(value);
          const image = colorImages?.[value];

          return (
            <label
              key={value}
              htmlFor={id}
              className={clsx(
                'cursor-pointer ml-[9px] mt-[9px] border-2 border-solid bg-gray-200 rounded-[10px] overflow-hidden relative',
                checked && 'border-black',
              )}
              title={value}
            >
              <input
                data-testid={DATA_TEST_IDS.INPUT(name)}
                className="sr-only"
                type="radio"
                id={id}
                name={`option[${name}]`}
                value={value}
                checked={checked}
                onChange={() => handleChange(name, value)}
              />
              {image?.url && (
                <Image
                  loading="lazy"
                  width={isWide ? 86 : 56}
                  height={isWide ? 86 : 56}
                  sizes={isWide ? '86px' : '56px'}
                  data={image}
                  alt={image.altText ?? value}
                />
              )}

              {isDiscounted && (
                <div
                  className={clsx(
                    'absolute bottom-0 left-0 right-0 w-full overflow-hidden text-center',
                    !checked && 'bg-[#526c8e]/75',
                    checked && 'bg-[#215aa8]/75',
                  )}
                >
                  <span className="text-xxs text-white font-bold uppercase">
                    Sale
                  </span>
                </div>
              )}
            </label>
          );
        })}
      </div>
      {error && (
        <div
          data-testid={DATA_TEST_IDS.ERROR}
          className="text-red-500 text-xs mt-1 absolute whitespace-nowrap"
        >
          This is a required field.
        </div>
      )}
    </div>
  );
}

function OptionsGrid({
  values,
  name,
  handleChange,
}: {
  values: string[];
  name: string;
  handleChange: (name: string, value: string) => void;
}) {
  const {selectedOptions} = useProductOptions();

  return (
    <>
      {values.map((value) => {
        const checked = selectedOptions![name] === value;
        const id = `option-${name}-${value}`;

        return (
          <label key={id} htmlFor={id}>
            <input
              data-testid={DATA_TEST_IDS.INPUT(name)}
              className="sr-only"
              type="radio"
              id={id}
              name={`option[${name}]`}
              value={value}
              checked={checked}
              onChange={() => handleChange(name, value)}
            />
            <div
              data-testid={DATA_TEST_IDS.CHECKBOX('checked')}
              className={`leading-none py-1 border-b-[1.5px] cursor-pointer transition-all duration-200 ${
                checked ? 'border-primary/50' : 'border-primary/0'
              }`}
            >
              {value}
            </div>
          </label>
        );
      })}
    </>
  );
}
